import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import api from '../services/api';
import { getListadoServiciosGeo, getClaseServicio, getTipoServicio } from '../services/azureApi';
import { useSocket } from '../context/SocketContext';
import { useAuth } from '../context/AuthContext';

// ─── Helpers de presentación ──────────────────────────────────────────────────

const PRIORIDAD = {
  critica: { label: 'Crítica', bg: '#fde8e8', color: '#8e1010', border: '#c0392b' },
  alta:    { label: 'Alta',    bg: '#fde8e8', color: '#c0392b', border: '#e74c3c' },
  media:   { label: 'Media',   bg: '#fef3e8', color: '#b7770d', border: '#e67e22' },
  baja:    { label: 'Baja',    bg: '#eafaf1', color: '#1a7a3c', border: '#27ae60' },
};

const ESTADO_INC = {
  pendiente: { label: 'Pendiente', bg: '#fff8e1', color: '#b8860b' },
  en_curso:  { label: 'En curso',  bg: '#fde8e8', color: '#c0392b' },
  cerrado:   { label: 'Cerrado',   bg: '#f0f0f0', color: '#7f8c8d' },
};

const TIPO_ICON = {
  incendio:          '🔥',
  rescate:           '🆘',
  accidente:         '🚗',
  material_peligroso:'☢️',
  otro:              '⚡',
};

/** Devuelve color, fondo e ícono según la descripción de la clase de servicio. */
function categoriaPorClase(nombre = '') {
  const n = nombre.toLowerCase();
  if (n.includes('incendio') || n.includes('conato') || n.includes('forestal'))
    return { color: '#c0392b', bg: '#fdeaea', icon: '🔥' };
  if (n.includes('rescate') || n.includes('barranco') || n.includes('extraviad') || n.includes('rastreo'))
    return { color: '#e67e22', bg: '#fef3e8', icon: '🆘' };
  if (n.includes('tránsito') || n.includes('transito') || n.includes('motorista') ||
      n.includes('atropellad') || n.includes('vehículo') || n.includes('vehiculo') ||
      n.includes('aéreo') || n.includes('aereo'))
    return { color: '#2980b9', bg: '#eaf3fb', icon: '🚗' };
  if (n.includes('médica') || n.includes('medica') || n.includes('traslado') ||
      n.includes('enfermedad') || n.includes('intoxicad') || n.includes('maternidad') ||
      n.includes('quemad') || n.includes('electrocutad') || n.includes('caída') ||
      n.includes('caida') || n.includes('mordid') || n.includes('fallecido') ||
      n.includes('violencia') || n.includes('agredid') || n.includes('trabajo'))
    return { color: '#27ae60', bg: '#eafaf1', icon: '🏥' };
  if (n.includes('agua') || n.includes('inundad') || n.includes('soterada') || n.includes('acuático'))
    return { color: '#1a6fa3', bg: '#e8f4fd', icon: '💧' };
  if (n.includes('animal') || n.includes('enjambre'))
    return { color: '#8e44ad', bg: '#f5eef8', icon: '🐾' };
  if (n.includes('prevención') || n.includes('prevencion') || n.includes('falsa'))
    return { color: '#7f8c8d', bg: '#f4f6f7', icon: '🛡️' };
  return { color: '#555', bg: '#f4f4f4', icon: '⚡' };
}


const ESTADOS_RESP = {
  en_camino_compania: { label: 'En camino a la compañía',               color: '#e67e22', bg: '#fef3e8', emoji: '🚒' },
  en_compania:        { label: 'Ya llegué a la compañía',               color: '#27ae60', bg: '#eafaf1', emoji: '🏠' },
  en_camino_escena:   { label: 'En camino a la escena con equipo completo', color: '#c0392b', bg: '#fde8e8', emoji: '🔥' },
};

function PrioridadBadge({ p }) {
  const cfg = PRIORIDAD[p] || PRIORIDAD.media;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 20,
      fontSize: 11, fontWeight: 700, letterSpacing: .3,
      background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
    }}>
      {cfg.label}
    </span>
  );
}

PrioridadBadge.propTypes = {
  p: PropTypes.string,
};
PrioridadBadge.defaultProps = { p: 'media' };

function EstadoBadge({ e, mapa }) {
  const cfg = mapa[e] || { label: e, bg: '#f0f0f0', color: '#555' };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 20,
      fontSize: 11, fontWeight: 700,
      background: cfg.bg, color: cfg.color,
    }}>
      {cfg.label || e?.replace(/_/g,' ')}
    </span>
  );
}

EstadoBadge.propTypes = {
  e:    PropTypes.string,
  mapa: PropTypes.object.isRequired,
};

// ─── Componente principal ─────────────────────────────────────────────────────

// Mapeo de Cod_Prioridad de Azure → texto interno
const COD_PRIORIDAD = { '1': 'baja', '2': 'baja', '3': 'media', '4': 'alta', '5': 'critica' };

const hoyStr  = () => new Date().toISOString().split('T')[0];
const ini2Str = () => { const d = new Date(); d.setDate(d.getDate() - 2); return d.toISOString().split('T')[0]; };

export default function Despacho() {
  const { sesion, esBombero } = useAuth();
  const [incidentes, setIncidentes] = useState([]);
  const [despachos,  setDespachos]  = useState([]);
  const [cargando,   setCargando]   = useState(true);
  const [error,      setError]      = useState('');

  // Catálogos para nombres descriptivos
  const [clases,        setClases]        = useState([]);
  const [tiposServicio, setTiposServicio] = useState([]);
  const catalogLoaded = useRef(false);

  // ─── Filtros (inputs) ────────────────────────────────────────────────────
  const [inputFechaIni, setInputFechaIni] = useState(ini2Str);
  const [inputFechaFin, setInputFechaFin] = useState(hoyStr);
  const [inputNombre,   setInputNombre]   = useState('');
  const [inputUnidad,   setInputUnidad]   = useState('');

  // filtros "aplicados" — solo cambian al hacer clic en Buscar
  const [filtros, setFiltros] = useState({
    fechaIni: ini2Str(),
    fechaFin: hoyStr(),
    nombre:   '',
  });

  const { socket } = useSocket();

  // Modal despacho
  const [modal,          setModal]          = useState(false);
  const [incSeleccionado, setIncSeleccionado] = useState(null);
  const [unidad,         setUnidad]         = useState('');
  const [despachando,    setDespachando]    = useState(false);
  const [errorModal,     setErrorModal]     = useState('');

  // Respuestas de disponibilidad del personal (solo mapa, el form vive en IncCard)
  const [respuestas, setRespuestas] = useState({});
  const [tab, setTab] = useState('pendiente'); // 'pendiente' | 'en_curso'

  const onRespuesta = (sid, data) => {
    setRespuestas(prev => {
      const key     = String(sid);
      const prevList = prev[key] || [];
      const idx = prevList.findIndex(r => r.carnet === data.carnet);
      const newList = idx >= 0 ? prevList.map((r, i) => i === idx ? data : r) : [...prevList, data];
      return { ...prev, [key]: newList };
    });
  };

  // ─── Carga de catálogos (una sola vez) ──────────────────────────────────
  useEffect(() => {
    if (catalogLoaded.current) return;
    catalogLoaded.current = true;
    const codCompania = sesion?.cod_compania?.[0] ?? 44;
    Promise.all([getClaseServicio(codCompania), getTipoServicio(codCompania)])
      .then(([claseRes, tipoRes]) => {
        setClases(claseRes.data || []);
        setTiposServicio(tipoRes.data || []);
      })
      .catch(() => { /* catálogos opcionales, sin bloquear */ });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Carga de datos ──────────────────────────────────────────────────────

  const cargar = useCallback(async () => {
    setCargando(true);
    setError('');
    try {
      const codCompania = sesion?.cod_compania?.[0] ? Number(sesion.cod_compania[0]) : 44;
      const ini = new Date(filtros.fechaIni + 'T00:00:00');
      const fin = new Date(filtros.fechaFin + 'T23:59:59');

      const [rAzure, rLocal, rDes, rResp] = await Promise.allSettled([
        getListadoServiciosGeo(codCompania, ini.toISOString(), fin.toISOString(), filtros.nombre),
        api.get('/servicios'),
        api.get('/despacho'),
        api.get('/respuestas'),
      ]);

      const azureList = rAzure.status === 'fulfilled' ? (rAzure.value.data || []) : [];
      const localList = rLocal.status === 'fulfilled' ? (rLocal.value.data || []) : [];

      // Siempre mostrar Azure como fuente principal + locales sin match en Azure
      const azureNoControls = new Set(azureList.map(s => String(s.NoControl)));

      const azureIncidents = azureList.map(s => {
        const local = localList.find(l => l.NoControl != null && String(l.NoControl) === String(s.NoControl));
        return {
          ...s,
          id:          local?.id ?? String(s.NoControl),
          noControl:   s.NoControl,
          tipo:        s.Cod_Servicio?.toString() || 'otro',
          descripcion: s.Observaciones || '',
          direccion:   s.Direccion,
          prioridad:   COD_PRIORIDAD[String(s.Cod_Prioridad)] || 'media',
          fechaInicio: s.Fecha_Servicio,
          estado:      local?.estado ?? 'pendiente',
          unidades:    s.Des_Unidades,
        };
      });

      // Incidentes locales sin NoControl en Azure (creados localmente, Azure falló o aún no tiene NoControl)
      const localOnly = localList
        .filter(l => !l.NoControl || !azureNoControls.has(String(l.NoControl)))
        .map(l => ({
          ...l,
          id:          l.id,
          tipo:        l.Cod_Servicio?.toString() || 'otro',
          descripcion: l.Observaciones || '',
          direccion:   l.Direccion,
          prioridad:   l.Prioridad?.toLowerCase() || 'media',
          fechaInicio: l.Fecha_Servicio,
          estado:      l.estado ?? 'pendiente',
        }));

      setIncidentes([...azureIncidents, ...localOnly]);

      if (azureList.length === 0 && rAzure.status === 'rejected')
        setError('No se pudo conectar con Azure. Mostrando datos locales.');

      setDespachos(rDes.status === 'fulfilled' ? (rDes.value.data || []) : []);

      if (rResp.status === 'fulfilled') {
        const map = {};
        (rResp.value.data || []).forEach(r => {
          const key = String(r.servicioId);
          if (!map[key]) map[key] = [];
          map[key].push(r);
        });
        setRespuestas(map);
      }
    } catch (err) {
      setError('Error al cargar datos.');
      console.error(err);
    } finally {
      setCargando(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sesion, filtros]);

  useEffect(() => { cargar(); }, [cargar]);

  // ─── Socket: nuevos incidentes en tiempo real ─────────────────────────────

  useEffect(() => {
    if (!socket) return;
    const onNuevo = (inc) => {
      setIncidentes(prev => prev.find(i => i.id === inc.id) ? prev : [inc, ...prev]);
    };
    const onActualizado = (inc) => {
      setIncidentes(prev => prev.map(i => i.id === inc.id ? { ...i, ...inc } : i));
    };
    socket.on('nuevo_incidente',      onNuevo);
    socket.on('incidente_actualizado', onActualizado);

    const onRespBombero = (data) => {
      const sid = data.servicioId || data.incidenteId;
      if (!sid) return;
      setRespuestas(prev => {
        const prevList = prev[sid] || [];
        const idx = prevList.findIndex(r => r.carnet === data.carnet);
        const newList = idx >= 0
          ? prevList.map((r, i) => i === idx ? { ...r, estado: data.estado } : r)
          : [...prevList, { id: Date.now().toString(), servicioId: sid, nombre: data.nombre, carnet: data.carnet, estado: data.estado }];
        return { ...prev, [sid]: newList };
      });
    };
    socket.on('respuesta_bombero', onRespBombero);

    return () => {
      socket.off('nuevo_incidente',      onNuevo);
      socket.off('incidente_actualizado', onActualizado);
      socket.off('respuesta_bombero',    onRespBombero);
    };
  }, [socket]);

  // ─── Despachar unidad ────────────────────────────────────────────────────

  const abrirModal = (inc) => {
    setIncSeleccionado(inc);
    setUnidad('');
    setErrorModal('');
    setModal(true);
  };

  const confirmarDespacho = async () => {
    if (!unidad.trim()) { setErrorModal('Ingresa la unidad a despachar.'); return; }
    setDespachando(true);
    setErrorModal('');
    try {
      await api.post('/despacho', {
        incidenteId:    incSeleccionado.id,
        unidadAsignada: unidad.trim(),
      });
      await api.patch(`/servicios/${incSeleccionado.id}/estado`, { estado: 'en_curso' });
      setModal(false);
      cargar();
    } catch (err) {
      setErrorModal(err.response?.data?.mensaje || err.response?.data?.error || 'Error al despachar.');
    } finally {
      setDespachando(false);
    }
  };

  // ─── Cambiar estado despacho ─────────────────────────────────────────────
  // (disponible para uso futuro)
  // const cambiarEstadoDespacho = async (id, nuevoEstado) => { ... };

  // ─── Cerrar incidente ────────────────────────────────────────────────────

  const cerrarIncidente = async (inc) => {
    if (!window.confirm(`¿Cerrar el incidente "${inc.tipo}" en ${inc.direccion}?`)) return;
    try {
      await api.patch(`/servicios/${inc.id}/estado`, { estado: 'cerrado' });
    } catch (err) {
      if (err.response?.status === 404) {
        // Incidente solo-Azure sin registro local: crear registro local con estado cerrado
        await api.post('/servicios', {
          NoControl:          inc.noControl ?? inc.NoControl,
          Cod_Compania:       sesion?.cod_compania?.[0] ? Number(sesion.cod_compania[0]) : 44,
          Cod_Servicio:       inc.Cod_Servicio,
          Fecha_Servicio:     inc.Fecha_Servicio || inc.fechaInicio,
          Nombre_Solicitante: inc.Nombre_Solicitante,
          Observaciones:      inc.Observaciones || inc.descripcion,
          Direccion:          inc.Direccion || inc.direccion || '—',
          Latitud:            inc.Latitud,
          Longitud:           inc.Longitud,
          Prioridad:          inc.prioridad || 'media',
          estado:             'cerrado',
        });
      } else {
        alert('Error al cerrar el incidente.');
        return;
      }
    }
    cargar();
  };

  // ─── Render ──────────────────────────────────────────────────────────────

  // Dual-key lookup (same as Respuestas) para cuando servicioId puede ser UUID o NoControl
  const getRespDesp = (inc) => {
    const byId  = respuestas[String(inc.id)] || [];
    const byNC  = respuestas[String(inc.noControl ?? inc.NoControl)] || [];
    if (byId.length) return byId;
    if (byNC.length) return byNC;
    return [];
  };

  const unidadFilt = (i) => !inputUnidad.trim() || (i.unidades || i.Des_Unidades || '').toLowerCase().includes(inputUnidad.trim().toLowerCase());
  const sortDesc   = (a, b) => new Date(b.fechaInicio || b.Fecha_Servicio || 0) - new Date(a.fechaInicio || a.Fecha_Servicio || 0);

  // Para bomberos: "pendiente" = este bombero aún no respondió (aunque otros sí lo hayan hecho)
  //                "en_curso"  = este bombero ya respondió
  // Para encargados: lógica original (pendiente = nadie respondió / en_curso = al menos uno)
  const esBomberoActual = esBombero();
  const yoRespondi = (inc) =>
    esBomberoActual &&
    getRespDesp(inc).some(
      r => r.carnet === sesion?.carnet || r.nombre === sesion?.usuario
    );

  const pendientes = incidentes
    .filter(i => i.estado !== 'cerrado')
    .filter(i => esBomberoActual ? !yoRespondi(i) : getRespDesp(i).length === 0)
    .filter(unidadFilt)
    .sort(sortDesc);
  const activos = incidentes
    .filter(i => i.estado !== 'cerrado')
    .filter(i => esBomberoActual ? yoRespondi(i) : getRespDesp(i).length > 0)
    .filter(unidadFilt)
    .sort(sortDesc);
  const cerrados = incidentes.filter(i => i.estado === 'cerrado').slice(0, 5);
  void cerrados; // reservado para historial futuro

  return (
    <div style={{ minHeight: '100vh', background: '#f5f6fa', padding: '24px 16px' }}>
      <style>{`
        @media (max-width: 1024px) {
          .despacho-container { padding: 20px 14px; }
          .despacho-header { flex-direction: column; align-items: flex-start; gap: 12px; }
          .despacho-header-title { font-size: 1.05rem; }
          .despacho-header-sub { font-size: .75rem; }
          .despacho-header-actions { width: 100%; flex-direction: row; }
          .despacho-counters { gap: 10px; }
          .despacho-counter { padding: 10px 16px; min-width: 110px; }
          .despacho-counter-val { font-size: 20px; }
          .despacho-counter-lbl { font-size: .75rem; }
          .despacho-cards-grid { grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); }
          .despacho-card-header { flex-direction: column; align-items: flex-start; }
          .despacho-modal { padding: 24px 20px; }
          .despacho-modal-title { font-size: 1.05rem; }
          .despacho-modal-desc { font-size: .85rem; }
          .despacho-table { font-size: .8rem; }
          .despacho-table th, .despacho-table td { padding: 8px 10px; }
          .despacho-table th { font-size: .7rem; }
        }

        @media (max-width: 768px) {
          .despacho-container { padding: 16px 12px; }
          .despacho-header { margin-bottom: 16px; }
          .despacho-header-actions { width: 100%; }
          .despacho-header-actions a, .despacho-header-actions button { font-size: .7rem; padding: 6px 12px; }
          .despacho-counters { gap: 8px; margin-bottom: 16px; }
          .despacho-counter { padding: 8px 12px; min-width: 90px; border-radius: 8px; }
          .despacho-counter-val { font-size: 18px; }
          .despacho-counter-lbl { font-size: .65rem; margin-top: 1px; }
          .despacho-section { margin-bottom: 18px; }
          .despacho-section-title { font-size: .9rem; }
          .despacho-section-count { font-size: .75rem; padding: 2px 6px; }
          .despacho-cards-grid { grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 10px; }
          .despacho-card { padding: 12px; border-radius: 8px; }
          .despacho-card-type { font-size: 12px; }
          .despacho-card-buttons { gap: 6px; }
          .despacho-card-button { font-size: .75rem; padding: 6px 0; }
          .despacho-error { font-size: .75rem; padding: 10px 12px; }
          .despacho-table-wrapper { overflow-x: auto; }
          .despacho-table { font-size: .7rem; }
          .despacho-table th, .despacho-table td { padding: 6px 8px; }
          .despacho-table th { font-size: .6rem; }
          .despacho-modal { padding: 18px 14px; max-width: 90vw; }
          .despacho-modal-title { font-size: .95rem; }
          .despacho-modal-desc { font-size: .75rem; }
          .despacho-modal-label { font-size: .75rem; margin-bottom: 4px; }
          .despacho-modal-input { font-size: .8rem; padding: 8px 10px; }
          .despacho-modal-buttons { gap: 8px; }
          .despacho-modal-button { font-size: .75rem; padding: 8px 0; }
          .despacho-closed-item { padding: 8px 10px; }
          .despacho-closed-icon { font-size: 16px; }
          .despacho-closed-text { font-size: .7rem; }
        }

        @media (max-width: 480px) {
          .despacho-container { padding: 12px 8px; }
          .despacho-icon-box { padding: 8px 10px; font-size: 18px; }
          .despacho-header-main { gap: 8px; }
          .despacho-header-title { font-size: .95rem; }
          .despacho-header-sub { font-size: .65rem; }
          .despacho-header-actions { gap: 6px; width: 100%; }
          .despacho-header-actions a, .despacho-header-actions button { flex: 1; font-size: .65rem; padding: 5px 8px; }
          .despacho-error { font-size: .65rem; padding: 8px 10px; margin-bottom: 12px; }
          .despacho-counters { gap: 6px; margin-bottom: 12px; }
          .despacho-counter { padding: 6px 8px; min-width: 75px; border-radius: 6px; font-size: .7rem; }
          .despacho-counter-val { font-size: 16px; }
          .despacho-counter-lbl { font-size: .55rem; }
          .despacho-section { margin-bottom: 14px; }
          .despacho-section-header { gap: 8px; margin-bottom: 10px; }
          .despacho-section-title { font-size: .8rem; font-weight: 600; }
          .despacho-section-count { font-size: .65rem; padding: 1px 5px; }
          .despacho-cards-grid { grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 8px; }
          .despacho-card { padding: 10px; border-radius: 6px; }
          .despacho-card-icon { font-size: 18px; }
          .despacho-card-type { font-size: .7rem; }
          .despacho-card-address { font-size: .65rem; }
          .despacho-card-description { font-size: .6rem; padding: 4px 6px; }
          .despacho-card-units { font-size: .65rem; }
          .despacho-card-time { font-size: .55rem; }
          .despacho-card-buttons { gap: 4px; margin-top: 6px; }
          .despacho-card-button { font-size: .65rem; padding: 5px 0; border-radius: 4px; }
          .despacho-empty { padding: 12px 10px; font-size: .65rem; border-radius: 6px; }
          .despacho-table-wrapper { overflow-x: auto; }
          .despacho-table { font-size: .6rem; }
          .despacho-table th { font-size: .55rem; padding: 4px 6px; }
          .despacho-table td { padding: 4px 6px; }
          .despacho-table-select { font-size: .6rem; padding: 3px 4px; }
          .despacho-modal-overlay { padding: 8px; }
          .despacho-modal { padding: 14px 10px; border-radius: 10px; }
          .despacho-modal-title { font-size: .85rem; margin-bottom: 4px; }
          .despacho-modal-desc { font-size: .65rem; margin-bottom: 12px; }
          .despacho-modal-badges { gap: 6px; margin-bottom: 10px; }
          .despacho-modal-info { font-size: .65rem; padding: 6px 8px; }
          .despacho-modal-label { font-size: .65rem; margin-bottom: 3px; }
          .despacho-modal-input { font-size: .75rem; padding: 6px 8px; }
          .despacho-modal-error { font-size: .65rem; padding: 6px 8px; margin-bottom: 8px; }
          .despacho-modal-buttons { gap: 6px; margin-top: 10px; }
          .despacho-modal-button { font-size: .7rem; padding: 6px 0; }
          .despacho-closed-list { gap: 6px; }
          .despacho-closed-item { padding: 6px 8px; border-radius: 6px; }
          .despacho-closed-icon { font-size: 14px; }
          .despacho-closed-content { gap: 8px; }
          .despacho-closed-type { font-size: .65rem; }
          .despacho-closed-address { font-size: .6rem; margin-left: 6px; }
        }
      `}</style>

      <div className="despacho-container">

        {/* ── HEADER ─────────────────────────────────────────────────────── */}
        <div className="despacho-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
          <div className="despacho-header-main" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div className="despacho-icon-box" style={{ background: '#c0392b', borderRadius: 12, padding: '10px 14px' }}>🚒</div>
            <div>
              <h1 className="despacho-header-title" style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#1a1a2e' }}>Despacho de Unidades</h1>
              <p className="despacho-header-sub" style={{ margin: 0, fontSize: 13, color: '#7f8c8d' }}>Cía. 44 Jalapa · Gestión de incidentes y unidades</p>
            </div>
          </div>
          <div className="despacho-header-actions" style={{ display: 'flex', gap: 10 }}>
            <button onClick={cargar} disabled={cargando} style={{ background: '#fff', border: '1px solid #dde1e7', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 13, color: '#555', fontWeight: 600 }}>
              {cargando ? '...' : '↻ Actualizar'}
            </button>
          </div>
        </div>

      {error && (
        <div className="despacho-error" style={{ background: '#fde8e8', border: '1px solid #c0392b', borderRadius: 10, padding: '12px 16px', marginBottom: 20, color: '#8e1010', fontSize: 13 }}>
          ⚠️ {error}
        </div>
      )}

      {/* ── FILTROS ────────────────────────────────────────────────────── */}
      <div style={{
        background: '#fff', border: '1px solid #e8eaf0', borderRadius: 12,
        padding: '16px 20px', marginBottom: 20,
        display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end',
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 140 }}>
          <label style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.06em' }}>
            Desde
          </label>
          <input
            type="date"
            value={inputFechaIni}
            max={inputFechaFin}
            onChange={e => setInputFechaIni(e.target.value)}
            style={{ padding: '8px 10px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 13, outline: 'none', background: '#f8fafc', color: '#1e293b' }}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 140 }}>
          <label style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.06em' }}>
            Hasta
          </label>
          <input
            type="date"
            value={inputFechaFin}
            min={inputFechaIni}
            onChange={e => setInputFechaFin(e.target.value)}
            style={{ padding: '8px 10px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 13, outline: 'none', background: '#f8fafc', color: '#1e293b' }}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 180 }}>
          <label style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.06em' }}>
            Nombre Solicitante
          </label>
          <input
            type="text"
            placeholder="Buscar por nombre..."
            value={inputNombre}
            onChange={e => setInputNombre(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && setFiltros({ fechaIni: inputFechaIni, fechaFin: inputFechaFin, nombre: inputNombre })}
            style={{ padding: '8px 10px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 13, outline: 'none', background: '#f8fafc', color: '#1e293b' }}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 160 }}>
          <label style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.06em' }}>
            Unidad
          </label>
          <input
            type="text"
            placeholder="Filtrar por unidad..."
            value={inputUnidad}
            onChange={e => setInputUnidad(e.target.value)}
            style={{ padding: '8px 10px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 13, outline: 'none', background: '#f8fafc', color: '#1e293b' }}
          />
        </div>
        <button
          onClick={() => setFiltros({ fechaIni: inputFechaIni, fechaFin: inputFechaFin, nombre: inputNombre })}
          disabled={cargando}
          style={{
            background: '#1e293b', color: '#fff', border: 'none',
            padding: '9px 20px', borderRadius: 8, fontSize: 13, fontWeight: 700,
            cursor: cargando ? 'not-allowed' : 'pointer', opacity: cargando ? .6 : 1,
            whiteSpace: 'nowrap',
          }}
        >
          {cargando ? '...' : '🔍 Buscar'}
        </button>
        {(filtros.nombre || filtros.fechaIni !== ini2Str() || filtros.fechaFin !== hoyStr()) && (
          <button
            onClick={() => {
              setInputFechaIni(ini2Str());
              setInputFechaFin(hoyStr());
              setInputNombre('');
              setInputUnidad('');
              setFiltros({ fechaIni: ini2Str(), fechaFin: hoyStr(), nombre: '' });
            }}
            style={{
              background: 'none', color: '#94a3b8', border: '1.5px solid #e2e8f0',
              padding: '9px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
              cursor: 'pointer', whiteSpace: 'nowrap',
            }}
          >
            ✕ Limpiar
          </button>
        )}
      </div>

      {/* ── CONTADORES ─────────────────────────────────────────────────── */}
      <div className="despacho-counters" style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {[
          { label: 'Pendientes', count: pendientes.length, color: '#e67e22', bg: '#fef3e8' },
          { label: 'En Curso',   count: activos.length,    color: '#c0392b', bg: '#fde8e8' },
          { label: 'Bomberos activos', count: Object.values(respuestas).flat().filter(r => r.estado === 'en_camino_compania' || r.estado === 'en_compania' || r.estado === 'en_camino_escena').length, color: '#27ae60', bg: '#eafaf1' },
        ].map(s => (
          <div key={s.label} className="despacho-counter" style={{ background: s.bg, border: `1px solid ${s.color}33`, borderRadius: 10, padding: '12px 20px', minWidth: 130 }}>
            <div className="despacho-counter-val" style={{ fontSize: 24, fontWeight: 800, color: s.color }}>{s.count}</div>
            <div className="despacho-counter-lbl" style={{ fontSize: 12, color: '#7f8c8d', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── TABS Pendientes / En Curso ──────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {[
          { key: 'pendiente', label: esBomberoActual ? '🔴 Por responder' : '🔴 Pendientes', count: pendientes.length, activeColor: '#e67e22' },
          { key: 'en_curso',  label: esBomberoActual ? '✅ Ya respondí'   : '🟠 En Curso',   count: activos.length,    activeColor: '#c0392b' },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: '9px 20px', borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700,
            background: tab === t.key ? t.activeColor : '#fff',
            color:      tab === t.key ? '#fff' : '#7f8c8d',
            boxShadow:  tab === t.key ? `0 2px 10px ${t.activeColor}55` : '0 1px 4px rgba(0,0,0,.06)',
            transition: 'all .15s',
          }}>
            {t.label}
            <span style={{
              marginLeft: 8, background: tab === t.key ? 'rgba(255,255,255,.3)' : '#f0f2f5',
              color: tab === t.key ? '#fff' : '#555',
              borderRadius: 20, padding: '1px 8px', fontSize: 11,
            }}>{t.count}</span>
          </button>
        ))}
      </div>

      {/* ── GRID ÚNICO filtrado por tab ─────────────────────────────────── */}
      {(() => {
        const lista = tab === 'pendiente' ? pendientes : activos;
        const empty = tab === 'pendiente'
          ? (esBomberoActual ? 'No tienes incidentes por responder.' : 'No hay incidentes pendientes.')
          : (esBomberoActual ? 'Aún no has respondido a ningún incidente.' : 'No hay incidentes en curso.');
        return lista.length === 0 ? (
          <EmptyMsg texto={empty} />
        ) : (
          <div className="despacho-cards-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
            {lista.map(inc => {
                // Primero tomar los textos guardados directamente en el registro (fuente fiable)
                // Si no existen (registros viejos), hacer fallback al catálogo en memoria
                const claseNombre = inc.Des_Clase_Servicio
                  || clases.find(c => String(c.Cod_Clase_Servicio) === String(inc.Cod_Clase_Servicio))?.Des_Clase_Servicio
                  || null;
                const tipoNombre = inc.Des_Tipo_Servicio
                  || tiposServicio.find(t => String(t.Cod_Servicio) === String(inc.Cod_Servicio))?.Descripcion_Servicio
                  || null;
                return (
                  <IncCard key={inc.id} inc={{ ...inc, claseNombre, tipoNombre }} onDespachar={() => abrirModal(inc)} onCerrar={() => cerrarIncidente(inc)} despachos={despachos} respuestasInc={getRespDesp(inc)} onRespuesta={onRespuesta} sesion={sesion} esBombero={esBombero()} />
                );
              })}
          </div>
        );
      })()}

      {/* ── MODAL DESPACHAR ─────────────────────────────────────────────── */}
      {modal && incSeleccionado && (
        <div className="despacho-modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={e => { if (e.target === e.currentTarget) setModal(false); }}>
          <div className="despacho-modal" style={{ background: '#fff', borderRadius: 16, padding: 28, width: '100%', maxWidth: 440, boxShadow: '0 20px 60px rgba(0,0,0,.25)' }}>
            <h2 className="despacho-modal-title" style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 800, color: '#1a1a2e' }}>🚒 Despachar Unidad</h2>
            <p className="despacho-modal-desc" style={{ margin: '0 0 20px', fontSize: 13, color: '#7f8c8d' }}>
              {TIPO_ICON[incSeleccionado.tipo]} {incSeleccionado.tipo?.replace(/_/g,' ')} · {incSeleccionado.direccion}
            </p>

            <div style={{ marginBottom: 8 }}>
              <div className="despacho-modal-badges" style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                <PrioridadBadge p={incSeleccionado.prioridad} />
                <EstadoBadge e={incSeleccionado.estado} mapa={ESTADO_INC} />
              </div>

              {incSeleccionado.descripcion && (
                <div className="despacho-modal-info" style={{ background: '#f5f6fa', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#555', marginBottom: 16 }}>
                  {incSeleccionado.descripcion}
                </div>
              )}
            </div>

            <label className="despacho-modal-label" style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 6 }}>
              Unidad a despachar *
            </label>
            <input
              className="despacho-modal-input"
              type="text"
              placeholder="Ej: Carro 1, Ambulancia 2, Cisterna..."
              value={unidad}
              onChange={e => setUnidad(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && confirmarDespacho()}
              style={{ width: '100%', border: '1px solid #dde1e7', borderRadius: 8, padding: '10px 14px', fontSize: 14, color: '#333', boxSizing: 'border-box', marginBottom: 6 }}
              autoFocus
            />

            {errorModal && (
              <div className="despacho-modal-error" style={{ background: '#fde8e8', color: '#8e1010', borderRadius: 6, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>
                ⚠️ {errorModal}
              </div>
            )}

            <div className="despacho-modal-buttons" style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button className="despacho-modal-button" onClick={() => setModal(false)} style={{ flex: 1, background: '#f5f6fa', border: '1px solid #dde1e7', borderRadius: 8, padding: '10px 0', fontSize: 14, cursor: 'pointer', color: '#555' }}>
                Cancelar
              </button>
              <button className="despacho-modal-button" onClick={confirmarDespacho} disabled={despachando} style={{ flex: 2, background: '#c0392b', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 0', fontSize: 14, fontWeight: 700, cursor: 'pointer', opacity: despachando ? .7 : 1 }}>
                {despachando ? 'Despachando...' : '🚒 Confirmar Despacho'}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

function Section({ className, title, count, children }) {
  return (
    <div className={className} style={{ marginBottom: 28 }}>
      <div className="despacho-section-header" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <h2 className="despacho-section-title" style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1a1a2e' }}>{title}</h2>
        <span className="despacho-section-count" style={{ background: '#1a1a2e', color: '#fff', borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>{count}</span>
      </div>
      {children}
    </div>
  );
}

Section.propTypes = {
  className: PropTypes.string,
  title:    PropTypes.string.isRequired,
  count:    PropTypes.number.isRequired,
  children: PropTypes.node.isRequired,
};

function EmptyMsg({ texto }) {
  return (
    <div className="despacho-empty" style={{ background: '#fff', border: '1px solid #eee', borderRadius: 10, padding: '20px 16px', textAlign: 'center', color: '#7f8c8d', fontSize: 13 }}>
      {texto}
    </div>
  );
}

EmptyMsg.propTypes = {
  texto: PropTypes.string.isRequired,
};

function InfoRow({ emoji, label, text, bold, color }) {
  if (!text) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 7 }}>
      <span style={{ fontSize: 13, flexShrink: 0, marginTop: 1 }}>{emoji}</span>
      <span style={{ fontSize: 12, lineHeight: 1.4, color: color || (bold ? '#1a1a2e' : '#475569'), fontWeight: bold ? 700 : 400 }}>
        {label && <span style={{ fontWeight: 600, color: '#64748b', marginRight: 3 }}>{label}:</span>}
        {text}
      </span>
    </div>
  );
}
InfoRow.propTypes = {
  emoji: PropTypes.string.isRequired,
  label: PropTypes.string,
  text:  PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  bold:  PropTypes.bool,
  color: PropTypes.string,
};

function IncCard({ inc, onDespachar, onCerrar, despachos, respuestasInc, onRespuesta, sesion, esBombero }) {
  const despachosInc = despachos.filter(d => d.incidenteId === inc.id && d.estado !== 'disponible');
  const pCfg = PRIORIDAD[inc.prioridad] || PRIORIDAD.media;

  // Estado del form (solo para encargados)
  const [showForm, setShowForm] = useState(false);
  const [nombre,   setNombre]   = useState(sesion?.usuario || '');
  const [carnet,   setCarnet]   = useState('');
  const [estado,   setEstado]   = useState('en_camino_compania');
  const [enviando, setEnviando] = useState(false);
  const [error,    setError]    = useState('');
  // Estado seleccionado (pendiente de confirmar) para bomberos
  const [estadoPendiente, setEstadoPendiente] = useState(null);

  // Respuesta previa de este bombero
  const miRespuesta = esBombero
    ? respuestasInc.find(r => r.carnet === sesion?.carnet || r.nombre === sesion?.usuario)
    : null;

  const nombreFinal = esBombero ? (sesion?.usuario || '') : nombre.trim();
  const carnetFinal = esBombero ? (sesion?.carnet  || '') : (carnet.trim() || nombre.trim());

  const guardar = async (estadoOverride) => {
    const est = estadoOverride || estado;
    if (!nombreFinal.trim()) { setError('El nombre es obligatorio.'); return; }
    setEnviando(true); setError('');
    try {
      const { data } = await api.post('/respuestas', {
        servicioId: String(inc.id),
        nombre: nombreFinal,
        carnet: carnetFinal || nombreFinal,
        estado: est,
      });
      onRespuesta(String(inc.id), data);
      if (esBombero) { setEstadoPendiente(null); }
      else { setNombre(''); setCarnet(''); setEstado('en_camino_compania'); setShowForm(false); }
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar.');
    } finally {
      setEnviando(false);
    }
  };

  const estCfg  = ESTADOS_RESP[estado] || ESTADOS_RESP.en_camino_compania;
  const initials = (str) => str?.trim().split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';

  const ESTADOS_LIST = [
    { key: 'en_camino_compania', label: 'En camino a la compañía',               emoji: '🚒', color: '#e67e22' },
    { key: 'en_compania',        label: 'Ya llegué a la compañía',               emoji: '🏠', color: '#27ae60' },
    { key: 'en_camino_escena',   label: 'En camino a la escena con equipo completo', emoji: '🔥', color: '#c0392b' },
  ];

  return (
    <div style={{
      background: '#fff',
      borderRadius: 14,
      overflow: 'hidden',
      boxShadow: '0 2px 16px rgba(0,0,0,.08)',
      borderTop: `4px solid ${pCfg.border}`,
      display: 'flex',
      flexDirection: 'column',
    }}>

      {/* ── CABECERA ─────────────────────────────────────────── */}
      <div style={{ padding: '14px 16px 0' }}>

        {/* Fila 1: icono tipo + título + badges */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, minWidth: 0 }}>
            {(() => {
              // Detectar categoría por clase, tipo, o como fallback por observaciones
              const clueText = inc.claseNombre || inc._claseServicio || inc.tipo || inc.Observaciones || inc.descripcion || '';
              const cat = categoriaPorClase(clueText);
              return (
                <div style={{
                  width: 48, height: 48, borderRadius: 14, flexShrink: 0,
                  background: cat.bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 26, border: `2px solid ${cat.color}30`,
                  boxShadow: `0 2px 8px ${cat.color}20`,
                }}>
                  {cat.icon}
                </div>
              );
            })()}
            <div style={{ minWidth: 0, paddingTop: 2 }}>
              {/* Tipo de Servicio como título principal */}
              <div style={{ fontWeight: 800, fontSize: 14, color: '#1a1a2e', lineHeight: 1.3 }}>
                {inc.tipoNombre || inc.claseNombre || inc._claseServicio || 'Sin tipo asignado'}
              </div>
              {/* Clase de incidente — chip de color debajo del título */}
              {inc.claseNombre && (() => {
                const cat = categoriaPorClase(inc.claseNombre);
                return (
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 4,
                    fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99,
                    background: cat.bg, color: cat.color, border: `1px solid ${cat.color}40`,
                    maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {cat.icon} {inc.claseNombre}
                  </span>
                );
              })()}
              {/* # NoControl — badge azul */}
              {(inc.NoControl || inc.noControl) && (
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 3, marginTop: 4,
                  fontSize: 11, color: '#1a6fa3', fontWeight: 700,
                  background: '#e8f4fd', borderRadius: 6, padding: '1px 7px',
                  border: '1px solid #bee3f8',
                }}>
                  # {inc.NoControl ?? inc.noControl}
                </div>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
            <PrioridadBadge p={inc.prioridad} />
            <EstadoBadge e={inc.estado} mapa={ESTADO_INC} />
            {inc._azureSource && (
              <span style={{ fontSize: 10, fontWeight: 700, background: '#e8f4fd', color: '#1a6fa3', padding: '2px 7px', borderRadius: 10, border: '1px solid #bee3f8' }}>☁ Azure</span>
            )}
          </div>
        </div>

        {/* Separador */}
        <div style={{ height: 1, background: '#f1f5f9', marginBottom: 10 }} />

        {/* ── Info del incidente ───────────────────────────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginBottom: 10 }}>

          {/* Fecha */}
          <InfoRow
            emoji="🕐"
            text={new Date(inc.createdAt || inc.Fecha_Servicio || inc.fechaInicio).toLocaleString('es-GT', {
              weekday: 'short', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
            })}
          />

          {/* Dirección / Lugar del Incidente */}
          <InfoRow emoji="📍" label="Dirección" text={inc.Direccion || inc.direccion || '—'} bold />

          {/* Unidades Requeridas */}
          {(inc.Des_Unidades || inc.Unidades || inc.unidades) && (
            <InfoRow emoji="🚒" label="Unidades" text={inc.Des_Unidades || inc.Unidades || inc.unidades} />
          )}

          {/* Solicitante */}
          {(inc.Nombre_Solicitante || inc.nombre_solicitante) && (
            <InfoRow emoji="👤" label="Solicitante" text={inc.Nombre_Solicitante || inc.nombre_solicitante} />
          )}

          {/* Registrado por — muy discreto */}
          {(inc.Usuario_Registra || inc.usuario_registra) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 1 }}>
              <span style={{ fontSize: 10 }}>✍️</span>
              <span style={{ fontSize: 10, color: '#94a3b8' }}>{inc.Usuario_Registra || inc.usuario_registra}</span>
            </div>
          )}
        </div>

        {/* 7. Observaciones — siempre visible si existe */}
        {(inc.Observaciones || inc.descripcion) && (
          <div style={{
            borderRadius: 8, padding: '9px 12px', marginBottom: 10,
            background: '#f8fafc', borderLeft: `3px solid ${pCfg.border}`,
            lineHeight: 1.6,
          }}>
            <span style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 3 }}>
              📝 Observaciones
            </span>
            <span style={{ fontSize: 12, color: '#475569' }}>
              {inc.Observaciones || inc.descripcion}
            </span>
          </div>
        )}

        {/* Botones de navegación — solo si hay coordenadas */}
        {(() => {
          const lat = parseFloat(inc.Latitud ?? inc.latitud);
          const lon = parseFloat(inc.Longitud ?? inc.longitud);
          if (!lat || !lon || isNaN(lat) || isNaN(lon)) return null;
          return (
            <div style={{ display: 'flex', gap: 7, marginBottom: 10 }}>
              <a
                href={`https://waze.com/ul?ll=${lat},${lon}&navigate=yes`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  background: '#33ccff', color: '#fff', textDecoration: 'none',
                  borderRadius: 8, padding: '7px 10px', fontSize: 12, fontWeight: 700,
                  boxShadow: '0 1px 4px rgba(51,204,255,.35)',
                }}>
                <img src="https://www.waze.com/favicon.ico" alt="" style={{ width: 14, height: 14 }} />
                Waze
              </a>
              <a
                href={`https://maps.google.com/?q=${lat},${lon}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  background: '#4285f4', color: '#fff', textDecoration: 'none',
                  borderRadius: 8, padding: '7px 10px', fontSize: 12, fontWeight: 700,
                  boxShadow: '0 1px 4px rgba(66,133,244,.35)',
                }}>
                🗺️ Google Maps
              </a>
            </div>
          );
        })()}

        {/* Unidades despachadas localmente */}
        {despachosInc.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 10 }}>
            {despachosInc.map((d, i) => (
              <span key={i} style={{ background: '#ebf5fb', color: '#1a6fa3', border: '1px solid #bee3f8', borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 700 }}>
                🚒 {d.unidadAsignada}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ── SECCIÓN PERSONAL ────────────────────────────────── */}
      <div style={{ borderTop: '1px solid #f0f2f5', margin: '0 16px' }} />
      <div style={{ padding: '10px 16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: respuestasInc.length > 0 ? 8 : 0 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#8a9bb0', textTransform: 'uppercase', letterSpacing: .6 }}>
            👥 Personal · {respuestasInc.length}
          </span>
          {!esBombero && (
            <button
              onClick={() => { setShowForm(f => !f); setError(''); }}
              style={{
                background: showForm ? '#f1f3f5' : '#1a1a2e',
                color:      showForm ? '#555'    : '#fff',
                border: 'none', borderRadius: 20,
                padding: '4px 13px', fontSize: 11, fontWeight: 700, cursor: 'pointer',
              }}>
              {showForm ? '✕ Cancelar' : '+ Registrar'}
            </button>
          )}
        </div>

        {respuestasInc.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 4 }}>
            {respuestasInc.map((r, i) => {
              const cfg = ESTADOS_RESP[r.estado] || { label: r.estado, color: '#555', bg: '#f0f0f0', emoji: '❓' };
              return (
                <div key={r.id || i} style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  background: cfg.bg, border: `1px solid ${cfg.color}30`,
                  borderRadius: 20, padding: '4px 10px 4px 4px',
                }}>
                  <div style={{ width: 24, height: 24, borderRadius: '50%', background: cfg.color, color: '#fff', fontSize: 10, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {initials(r.nombre)}
                  </div>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#1a1a2e', whiteSpace: 'nowrap', maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {r.nombre}{r.carnet && r.carnet !== r.nombre ? <span style={{ color: '#aaa', fontWeight: 400 }}> · {r.carnet}</span> : ''}
                    </div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: cfg.color }}>{cfg.emoji} {cfg.label}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {respuestasInc.length === 0 && !showForm && !esBombero && (
          <p style={{ fontSize: 11, color: '#c8cfd8', margin: 0, textAlign: 'center', padding: '4px 0' }}>Sin respuestas aún</p>
        )}

        {/* Form encargado (toggle) */}
        {!esBombero && showForm && (
          <div style={{ marginTop: 10, padding: '14px', background: '#f8fafc', borderRadius: 10, border: `1px solid ${estCfg.color}30` }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <div style={{ flex: 2 }}>
                <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#8a9bb0', marginBottom: 4, textTransform: 'uppercase' }}>Nombre *</label>
                <input type="text" placeholder="Juan Pérez" value={nombre} onChange={e => setNombre(e.target.value)}
                  style={{ width: '100%', border: `1.5px solid ${nombre ? estCfg.color + '80' : '#dde1e7'}`, borderRadius: 8, padding: '8px 10px', fontSize: 13, boxSizing: 'border-box', outline: 'none' }} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#8a9bb0', marginBottom: 4, textTransform: 'uppercase' }}>Carnet</label>
                <input type="text" placeholder="A-123" value={carnet} onChange={e => setCarnet(e.target.value)}
                  style={{ width: '100%', border: '1.5px solid #dde1e7', borderRadius: 8, padding: '8px 10px', fontSize: 13, boxSizing: 'border-box', outline: 'none' }} />
              </div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
              {Object.entries(ESTADOS_RESP).map(([k, v]) => (
                <button key={k} onClick={() => setEstado(k)} style={{
                  background: estado === k ? v.color : '#fff', color: estado === k ? '#fff' : '#555',
                  border: `2px solid ${estado === k ? v.color : '#e0e4ea'}`, borderRadius: 20,
                  padding: '5px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer',
                }}>
                  <span style={{ fontSize: 13 }}>{v.emoji}</span> {v.label}
                </button>
              ))}
            </div>
            {error && <div style={{ background: '#fde8e8', color: '#8e1010', borderRadius: 8, padding: '6px 10px', fontSize: 12, marginBottom: 8 }}>⚠️ {error}</div>}
            <button onClick={() => guardar()} disabled={enviando} style={{
              width: '100%', background: estCfg.color, color: '#fff', border: 'none',
              borderRadius: 8, padding: '9px 0', fontSize: 13, fontWeight: 800, cursor: 'pointer', opacity: enviando ? .7 : 1,
            }}>
              {enviando ? '⏳ Guardando...' : `${estCfg.emoji} Confirmar`}
            </button>
          </div>
        )}
      </div>

      {/* ── RESPUESTA RÁPIDA BOMBERO ─────────────────────────── */}
      {esBombero && (
        <div style={{
          borderTop: miRespuesta ? `2px solid ${(ESTADOS_RESP[miRespuesta.estado] || ESTADOS_RESP.en_camino_compania).color}` : '1px solid #f0f2f5',
          background: miRespuesta ? (ESTADOS_RESP[miRespuesta.estado] || ESTADOS_RESP.en_camino_compania).bg : '#f8fafc',
          padding: '12px 16px',
        }}>
          {/* Identidad del bombero */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#1a1a2e', color: '#fff', fontSize: 11, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {initials(sesion?.usuario)}
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#1a1a2e' }}>{sesion?.usuario || 'Bombero'}</div>
              {sesion?.carnet && <div style={{ fontSize: 10, color: '#888' }}>🪪 {sesion.carnet}</div>}
            </div>
            {miRespuesta && (
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: (ESTADOS_RESP[miRespuesta.estado] || {}).color }}>
                  {(ESTADOS_RESP[miRespuesta.estado] || {}).emoji} Tu respuesta actual
                </span>
              </div>
            )}
          </div>

          {/* Pills de estado — seleccionar luego confirmar */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {ESTADOS_LIST.map(e => {
              const esActual    = miRespuesta?.estado === e.key;
              const esPendiente = estadoPendiente === e.key;
              return (
                <button
                  key={e.key}
                  disabled={enviando}
                  onClick={() => setEstadoPendiente(esPendiente ? null : e.key)}
                  style={{
                    background: esPendiente ? e.color : esActual ? `${e.color}22` : '#fff',
                    color:      esPendiente ? '#fff'  : esActual ? e.color          : '#444',
                    border: `2px solid ${esPendiente || esActual ? e.color : '#e0e4ea'}`,
                    borderRadius: 20, padding: '6px 13px',
                    fontSize: 12, fontWeight: 700, cursor: enviando ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', gap: 5,
                    boxShadow: esPendiente ? `0 2px 8px ${e.color}50` : 'none',
                    transition: 'all .15s',
                    opacity: enviando ? .6 : 1,
                  }}>
                  <span style={{ fontSize: 14 }}>{e.emoji}</span> {e.label}
                </button>
              );
            })}
          </div>

          {/* Botón confirmar — solo aparece cuando hay selección pendiente */}
          {estadoPendiente && (() => {
            const eCfg = ESTADOS_LIST.find(e => e.key === estadoPendiente);
            return (
              <button
                onClick={() => guardar(estadoPendiente)}
                disabled={enviando}
                style={{
                  marginTop: 10, width: '100%',
                  background: `linear-gradient(135deg, ${eCfg.color}, ${eCfg.color}cc)`,
                  color: '#fff', border: 'none', borderRadius: 9,
                  padding: '11px 0', fontSize: 13, fontWeight: 800,
                  cursor: enviando ? 'not-allowed' : 'pointer',
                  boxShadow: `0 3px 10px ${eCfg.color}60`,
                  transition: 'all .2s', opacity: enviando ? .7 : 1,
                }}>
                {enviando ? '⏳ Enviando...' : `${eCfg.emoji} Confirmar — ${eCfg.label}`}
              </button>
            );
          })()}

          {error && <div style={{ background: '#fde8e8', color: '#8e1010', borderRadius: 8, padding: '6px 10px', fontSize: 11, marginTop: 8 }}>⚠️ {error}</div>}
        </div>
      )}

      {/* ── BOTONES ACCIÓN (solo encargado) ─────────────────── */}
      {!esBombero && (
        <div style={{ display: 'flex', gap: 8, padding: '0 16px 14px', marginTop: 4 }}>
          <button onClick={onDespachar} style={{ flex: 2, background: 'linear-gradient(135deg,#c0392b,#e74c3c)', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 0', fontSize: 13, fontWeight: 700, cursor: 'pointer', boxShadow: '0 2px 8px rgba(192,57,43,.35)' }}>
            🚒 Despachar
          </button>
          <button onClick={onCerrar} style={{ flex: 1, background: '#f5f6fa', border: '1px solid #dde1e7', borderRadius: 8, padding: '10px 0', fontSize: 12, color: '#7f8c8d', cursor: 'pointer', fontWeight: 600 }}>
            ✓ Cerrar
          </button>
        </div>
      )}
    </div>
  );
}

IncCard.propTypes = {
  inc: PropTypes.shape({
    id:                 PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    noControl:          PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    NoControl:          PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    prioridad:          PropTypes.string,
    tipo:               PropTypes.string,
    estado:             PropTypes.string,
    direccion:          PropTypes.string,
    Direccion:          PropTypes.string,
    descripcion:        PropTypes.string,
    Observaciones:      PropTypes.string,
    createdAt:          PropTypes.string,
    fechaInicio:        PropTypes.string,
    Fecha_Servicio:     PropTypes.string,
    Nombre_Solicitante: PropTypes.string,
    Zona:               PropTypes.string,
    zona:               PropTypes.string,
    Area:               PropTypes.string,
    area:               PropTypes.string,
    Des_Unidades:       PropTypes.string,
    Unidades:           PropTypes.string,
    unidades:           PropTypes.string,
    Des_Lugar:          PropTypes.string,
    Usuario_Registra:   PropTypes.string,
    usuario_registra:   PropTypes.string,
    nombre_solicitante: PropTypes.string,
    _azureSource:       PropTypes.bool,
    _claseServicio:     PropTypes.string,
    claseNombre:        PropTypes.string,
    tipoNombre:         PropTypes.string,
    Des_Clase_Servicio: PropTypes.string,
    Des_Tipo_Servicio:  PropTypes.string,
    Latitud:            PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    Longitud:           PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    latitud:            PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    longitud:           PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  onDespachar:   PropTypes.func.isRequired,
  onCerrar:      PropTypes.func.isRequired,
  despachos:     PropTypes.arrayOf(PropTypes.object).isRequired,
  respuestasInc: PropTypes.arrayOf(PropTypes.object),
  onRespuesta:   PropTypes.func.isRequired,
  sesion:        PropTypes.object,
  esBombero:     PropTypes.bool,
};

IncCard.defaultProps = {
  respuestasInc: [],
  sesion:        null,
  esBombero:     false,
};
